import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, CardMedia, Chip, Button, TextField, InputAdornment } from '@mui/material';
import { AccessTime, ArrowForward, Search, TrendingUp, Article, Security, Business, Build, Assessment } from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const categories = [
  { label: 'All', value: 'all' },
  { label: 'Product', value: 'Product' },
  { label: 'Technical', value: 'Technical' },
  { label: 'Security', value: 'Security' },
  { label: 'Industry', value: 'Industry' },
  { label: 'Case Study', value: 'Case Study' },
];

const categoryColors: Record<string, { bg: string; text: string }> = {
  Product: { bg: '#fee2e2', text: '#dc2626' },
  Technical: { bg: '#dbeafe', text: '#1d4ed8' },
  Security: { bg: '#d1fae5', text: '#065f46' },
  Industry: { bg: '#fef9c3', text: '#92400e' },
  'Case Study': { bg: '#ede9fe', text: '#5b21b6' },
};

const featured = {
  title: 'Why Businesses Are Moving Away from Gmail in 2026',
  excerpt: 'Privacy concerns, rising costs, and vendor lock-in are driving organizations to explore alternatives. We break down the top reasons businesses are switching to private email platforms — and what to look for in a replacement.',
  category: 'Industry',
  date: 'Apr 18, 2026',
  readTime: '6 min read',
  color: '#0f172a',
};

const posts = [
  {
    title: 'Setting Up SPF, DKIM, and DMARC: The Complete Guide',
    excerpt: 'Email authentication is crucial for deliverability. Our step-by-step guide covers everything from DNS records to testing and monitoring.',
    category: 'Technical',
    date: 'Apr 10, 2026',
    readTime: '8 min read',
    accent: '#dbeafe',
    icon: <Build sx={{ fontSize: 28, color: '#1d4ed8' }} />,
  },
  {
    title: 'CruvzMail v2.5: AI Compose, DriveX & More',
    excerpt: 'Our biggest update yet brings AI-powered email composition, integrated file storage, calendar sync, and major performance improvements.',
    category: 'Product',
    date: 'Apr 5, 2026',
    readTime: '4 min read',
    accent: '#fee2e2',
    icon: <TrendingUp sx={{ fontSize: 28, color: '#dc2626' }} />,
  },
  {
    title: 'Email Security Best Practices for 2026',
    excerpt: 'From two-factor authentication to zero-trust architecture — the essential security measures every email admin should implement today.',
    category: 'Security',
    date: 'Mar 28, 2026',
    readTime: '6 min read',
    accent: '#d1fae5',
    icon: <Security sx={{ fontSize: 28, color: '#065f46' }} />,
  },
  {
    title: 'Migrating 10,000 Mailboxes: A Case Study',
    excerpt: 'How a mid-size company migrated their entire email infrastructure to CruvzMail over a weekend with zero data loss.',
    category: 'Case Study',
    date: 'Mar 20, 2026',
    readTime: '7 min read',
    accent: '#ede9fe',
    icon: <Assessment sx={{ fontSize: 28, color: '#5b21b6' }} />,
  },
  {
    title: 'How Multi-Domain Email Management Works',
    excerpt: 'Host dozens of domains under a single CruvzMail organisation. This guide covers domain verification, DKIM setup, and per-domain branding.',
    category: 'Technical',
    date: 'Mar 12, 2026',
    readTime: '5 min read',
    accent: '#dbeafe',
    icon: <Article sx={{ fontSize: 28, color: '#1d4ed8' }} />,
  },
  {
    title: 'The True Cost of "Free" Email for Business',
    excerpt: 'When email is free, you\'re the product. We break down the hidden costs of ad-supported email services and show you what you\'re actually paying.',
    category: 'Industry',
    date: 'Mar 5, 2026',
    readTime: '5 min read',
    accent: '#fef9c3',
    icon: <Business sx={{ fontSize: 28, color: '#92400e' }} />,
  },
];

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = posts.filter((p) => {
    const matchCat = activeCategory === 'all' || p.category === activeCategory;
    const matchSearch = !search.trim() || p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <PublicLayout>

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #1e0a0a 100%)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -150, right: -150, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -100, left: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 820, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="Blog" sx={{ mb: 3, bgcolor: 'rgba(220,38,38,0.15)', color: '#fca5a5', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(220,38,38,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.8rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Insights & Updates
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.6)', maxWidth: 560, mx: 'auto', lineHeight: 1.85, mb: 5 }}>
            Tips, tutorials, product news, and deep-dives on modern business email.
          </Typography>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles..."
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8' }} /></InputAdornment> }}
            sx={{
              maxWidth: 480, width: '100%',
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff', borderRadius: '12px', fontSize: '1rem', boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </Box>
      </Box>

      {/* ── FEATURED POST ── */}
      <Box sx={{ bgcolor: '#0f172a', pt: 6, pb: 0, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 3 }}>Featured</Typography>
          <Box
            onClick={() => navigate('/blog')}
            sx={{
              borderRadius: 3, overflow: 'hidden', cursor: 'pointer', position: 'relative',
              background: 'linear-gradient(135deg, #1e293b 0%, #1e0a0a 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', flexDirection: { xs: 'column', md: 'row' },
              mb: 6, transition: 'all 0.25s',
              '&:hover': { borderColor: 'rgba(220,38,38,0.35)', boxShadow: '0 12px 40px rgba(220,38,38,0.1)' },
            }}
          >
            <Box sx={{
              width: { xs: '100%', md: 380 }, minHeight: { xs: 180, md: 240 }, flexShrink: 0,
              background: 'linear-gradient(135deg, rgba(220,38,38,0.2) 0%, rgba(99,102,241,0.15) 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ fontSize: '5rem', fontWeight: 900, color: 'rgba(255,255,255,0.06)', userSelect: 'none' }}>✉</Typography>
            </Box>
            <Box sx={{ p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <Chip label={featured.category} size="small" sx={{ bgcolor: categoryColors[featured.category]?.bg ?? '#f1f5f9', color: categoryColors[featured.category]?.text ?? '#0f172a', fontSize: '0.7rem', fontWeight: 700 }} />
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>{featured.date}</Typography>
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.3rem', md: '1.7rem' }, color: '#fff', mb: 2, lineHeight: 1.3 }}>{featured.title}</Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.75, mb: 3 }}>{featured.excerpt}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'rgba(255,255,255,0.35)' }}>
                  <AccessTime sx={{ fontSize: 14 }} />
                  <Typography sx={{ fontSize: '0.78rem' }}>{featured.readTime}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#fca5a5', fontWeight: 600, fontSize: '0.82rem' }}>
                  Read article <ArrowForward sx={{ fontSize: 14 }} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── CATEGORY FILTER ── */}
      <Box sx={{ bgcolor: '#f8fafc', py: 3, px: { xs: 2, md: 5 }, borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {categories.map((c) => (
            <Chip
              key={c.value}
              label={c.label}
              onClick={() => setActiveCategory(c.value)}
              sx={{
                cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                bgcolor: activeCategory === c.value ? '#dc2626' : '#fff',
                color: activeCategory === c.value ? '#fff' : '#64748b',
                border: activeCategory === c.value ? '1.5px solid #dc2626' : '1.5px solid #e2e8f0',
                '&:hover': { bgcolor: activeCategory === c.value ? '#b91c1c' : '#fef2f2', borderColor: '#dc2626', color: activeCategory === c.value ? '#fff' : '#dc2626' },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* ── POSTS GRID ── */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          {filtered.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <Typography sx={{ fontSize: '1.1rem', color: '#94a3b8' }}>No articles found. Try a different search or category.</Typography>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {filtered.map((post) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={post.title}>
                  <Card elevation={0} sx={{
                    height: '100%', borderRadius: 3, border: '1px solid #e2e8f0',
                    cursor: 'pointer', transition: 'all 0.25s', display: 'flex', flexDirection: 'column',
                    '&:hover': { borderColor: '#fecaca', transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(220,38,38,0.07)' },
                  }}>
                    <CardMedia sx={{ height: 150, bgcolor: post.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ p: 2, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.7)' }}>{post.icon}</Box>
                    </CardMedia>
                    <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Chip
                          label={post.category} size="small"
                          sx={{ bgcolor: categoryColors[post.category]?.bg ?? '#f8fafc', color: categoryColors[post.category]?.text ?? '#64748b', fontSize: '0.7rem', fontWeight: 700 }}
                        />
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{post.date}</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a', mb: 1, lineHeight: 1.45 }}>{post.title}</Typography>
                      <Typography sx={{ fontSize: '0.84rem', color: '#64748b', lineHeight: 1.72, flex: 1 }}>{post.excerpt}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#94a3b8' }}>
                          <AccessTime sx={{ fontSize: 13 }} />
                          <Typography sx={{ fontSize: '0.72rem' }}>{post.readTime}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#dc2626', fontSize: '0.75rem', fontWeight: 600 }}>
                          Read more <ArrowForward sx={{ fontSize: 13 }} />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>

      {/* ── NEWSLETTER / CTA ── */}
      <Box sx={{
        py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #1e0a0a 100%)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 560, mx: 'auto' }}>
          <Typography variant="h3" sx={{
            fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 2,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>Stay in the Loop</Typography>
          <Typography sx={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.6)', mb: 5 }}>
            Get the latest product updates, email security tips, and industry news.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' }, maxWidth: 480, mx: 'auto' }}>
            <TextField
              placeholder="Your work email"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '10px', color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '& input::placeholder': { color: 'rgba(255,255,255,0.35)' },
                },
              }}
            />
            <Button onClick={() => navigate('/contact')} sx={{
              px: 3, py: 1.5, borderRadius: '10px', fontWeight: 700, fontSize: '0.9rem', textTransform: 'none',
              bgcolor: '#dc2626', color: '#fff', whiteSpace: 'nowrap', flexShrink: 0,
              '&:hover': { bgcolor: '#b91c1c' },
            }}>Subscribe</Button>
          </Box>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default BlogPage;
